
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Bell, ExternalLink, Share2 } from 'lucide-react';
import { getProductTitle } from '../../utils/dataFallbacks';
import { publicFeatures } from '../../config/publicFeatures';

export function StickyProductActions({ 
  product,
  isVisible, 
  onWishlistToggle, 
  isWishlisted, 
  onShare 
}) {
  if (!isVisible || !product) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-gray-700 shadow-lg z-40 p-4"
        data-analytics-id="sticky-product-actions"
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Product Info */}
          <div className="w-full flex items-center gap-3 flex-1 min-w-0">
            <img 
              src={product.image_url} 
              alt={`${product.brand || ''} ${getProductTitle(product)} - Product thumbnail`}
              className="w-12 h-12 object-contain bg-gray-50 dark:bg-gray-700 rounded-lg flex-shrink-0"
              loading="lazy"
            />
            <div className="min-w-0 flex-1">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                {getProductTitle(product)}
              </h4>
              <p className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                ${product.price}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onWishlistToggle}
              className={`p-2 rounded-lg transition-colors ${
                isWishlisted 
                  ? 'bg-red-50 text-red-600 border border-red-200' 
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
              data-analytics-id="sticky-wishlist-toggle"
            >
              <Heart size={18} className={isWishlisted ? 'fill-current' : ''} aria-hidden="true" />
            </button>

            {publicFeatures.priceAlerts && (
            <button
              className="p-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Set price alert"
              data-analytics-id="sticky-price-alert"
            >
              <Bell size={18} aria-hidden="true" />
            </button>
            )}

            <button
              onClick={onShare}
              className="p-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Share product"
              data-analytics-id="sticky-share"
            >
              <Share2 size={18} aria-hidden="true" />
            </button>

            <a
              href={`https://www.amazon.com/dp/${product.asin}?tag=${import.meta.env.VITE_AMAZON_PARTNER_TAG}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-yellow-400 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              aria-label="View product on Amazon"
              data-analytics-id="sticky-amazon-link"
            >
              <span className="hidden sm:inline">View on Amazon</span>
              <ExternalLink size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
